import clsx from 'clsx';
import React, { useState, useMemo, useEffect, type SetStateAction, type Dispatch } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { Checkbox } from '../catalyst/checkbox';

export interface SortableTableColumn<T> {
  label: string;
  accessor: keyof T | string;
  sortable?: boolean;
  /** Derive the value to sort by. Use when the raw accessor value isn't directly comparable (e.g. an array to sort by length). */
  sortValue?: (row: T) => string | number;
  render?: (row: T, index: number, array: T[]) => React.ReactNode;
  footer?: (rows: T[]) => React.ReactNode;
  className?: string;
  enabled?: boolean;
}

export interface SortableTableProps<T> {
  columns: SortableTableColumn<T>[];
  data: T[];
  defaultSortBy?: string;
  defaultSortDir?: 'asc' | 'desc';
  rowKey: (row: T, index: number) => string;
  rowClassName?: (row: T) => string;
  /** Unique, stable id namespacing the persisted sort state in localStorage. */
  storageId: string;
  limitHeight?: boolean;
  onChangeSelection?: (selectedRows: Set<string>) => void;
  selectedRows?: Set<string>;
  setSelectedRows?: Dispatch<SetStateAction<Set<string>>>;
  /** Called when a data cell of a row is clicked (not the selection checkbox). */
  onRowClick?: (row: T) => void;
  onRowMouseEnter?: (row: T, e: React.MouseEvent<HTMLTableRowElement>) => void;
  onRowMouseLeave?: () => void;
  /**
   * Makes the whole row navigate to the returned href on click (cmd/ctrl/shift-click
   * opens a new tab). Keep a real <Link> in one of the cells for middle-click support.
   */
  rowHref?: (row: T) => string;
  className?: string;
}

export function SortableTable<T>({
  columns,
  data,
  defaultSortBy,
  defaultSortDir = 'asc',
  rowKey,
  rowClassName,
  storageId,
  limitHeight = true,
  selectedRows,
  setSelectedRows,
  onRowClick,
  rowHref,
  onRowMouseEnter,
  onRowMouseLeave,
  className,
}: SortableTableProps<T>) {
  const navigate = useNavigate();
  const sortByManager = localStorageManager(
    storageId,
    'sortBy',
    defaultSortBy ?? (columns.find((c) => c.sortable)?.accessor as string) ?? '',
  );
  const sortDirManager = localStorageManager(storageId, 'sortDir', defaultSortDir);
  const [sortBy, setSortBy] = useState<string>(sortByManager.read());
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(sortDirManager.read());

  useEffect(() => {
    sortByManager.write(sortBy);
    sortDirManager.write(sortDir);
  }, [sortBy, sortDir]);

  const sortedData = useMemo(() => {
    if (!sortBy) return data;
    const col = columns.find((c) => c.accessor === sortBy);
    if (!col) return data;
    const sorted = [...data].sort((a, b) => {
      let aValue = col.sortValue ? col.sortValue(a) : (a as Record<string, unknown>)[sortBy as string];
      let bValue = col.sortValue ? col.sortValue(b) : (b as Record<string, unknown>)[sortBy as string];
      // Try to handle dates and numbers.
      // If aValue parses as a date, assume bValue is the same column type and
      // coerce it too (Date() accepts a number or string; we cast away the
      // type-narrowing gap between aValue and bValue).
      if (typeof aValue === 'string' && !isNaN(Date.parse(aValue))) {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue as string | number).getTime();
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDir === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortDir === 'asc' ? Number(aValue) - Number(bValue) : Number(bValue) - Number(aValue);
      }
      // Fallback to string comparison
      return sortDir === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    return sorted;
  }, [data, sortBy, sortDir, columns]);

  const columnsFiltered = useMemo(() => {
    return columns.filter((col) => col.enabled !== false);
  }, [columns]);

  const hasFooter = useMemo(() => columnsFiltered.some((col) => col.footer), [columnsFiltered]);

  const handleSort = (accessor: string, sortable?: boolean) => {
    if (!sortable) return;
    if (sortBy === accessor) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(accessor);
      setSortDir('desc');
    }
  };

  const handleSelectAllClick = (checked: boolean) => {
    setSelectedRows?.(() => {
      const newSet = new Set(checked ? sortedData.map(rowKey) : new Set<string>());
      return newSet;
    });
  };

  const toggleRowSelection = (row: T) => {
    setSelectedRows?.((prev) => {
      const newSet = new Set(prev);
      const index = sortedData.indexOf(row);
      if (newSet.has(rowKey(row, index))) {
        newSet.delete(rowKey(row, index));
      } else {
        newSet.add(rowKey(row, index));
      }
      return newSet;
    });
  };

  const handleDataCellClick = (row: T, event: React.MouseEvent) => {
    if (onRowClick) {
      onRowClick(row);
      return;
    }
    if (rowHref) {
      const href = rowHref(row);
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        window.open(href, '_blank', 'noopener');
      } else {
        navigate(href);
      }
      return;
    }
    toggleRowSelection(row);
  };

  return (
    <div
      className={twMerge(
        clsx(
          'overflow-y-auto',
          {
            'max-h-[650px]': limitHeight,
          },
          className,
        ),
      )}
    >
      <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
        <thead className="bg-gray-50 dark:bg-zinc-900 sticky top-0">
          <tr>
            {setSelectedRows && (
              <th className="ps-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-zinc-200 uppercase tracking-wider">
                <Checkbox
                  checked={selectedRows?.size === sortedData.length}
                  onChange={(checked) => {
                    handleSelectAllClick(checked);
                  }}
                />
              </th>
            )}
            {columnsFiltered.map((col) => (
              <th
                key={col.accessor as string}
                className={twMerge(
                  'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-zinc-200 uppercase tracking-wider',
                  col.sortable && 'cursor-pointer select-none',
                  col.className,
                )}
                onClick={() => handleSort(col.accessor as string, col.sortable)}
              >
                {col.label}
                {col.sortable && sortBy === col.accessor && (
                  <span className="ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-zinc-800 divide-y divide-gray-200 dark:divide-zinc-700">
          {sortedData.map((row, index) => (
            <tr
              key={rowKey(row, index)}
              className={twMerge(
                clsx('hover:bg-gray-50 dark:hover:bg-zinc-900', (onRowClick || rowHref) && 'cursor-pointer'),
                rowClassName ? rowClassName(row) : '',
              )}
              onMouseEnter={onRowMouseEnter ? (e) => onRowMouseEnter(row, e) : undefined}
              onMouseLeave={onRowMouseLeave}
            >
              {setSelectedRows && (
                <td className="ps-6 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-zinc-100 ">
                  <Checkbox
                    checked={selectedRows?.has(rowKey(row, index))}
                    onChange={() => {
                      toggleRowSelection(row);
                    }}
                  />
                </td>
              )}
              {columnsFiltered.map((col) => (
                <td
                  key={col.accessor as string}
                  className="px-6 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-zinc-100 "
                  onClick={(event) => {
                    handleDataCellClick(row, event);
                  }}
                >
                  {col.render ? col.render(row, index, sortedData) : String((row as Record<string, unknown>)[col.accessor as string] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {hasFooter && (
          <tfoot className="bg-gray-50 dark:bg-zinc-700 sticky bottom-0 border-t-2 border-gray-200 dark:border-zinc-600">
            <tr>
              {setSelectedRows && <td className="ps-6 py-3" />}
              {columnsFiltered.map((col) => (
                <td
                  key={col.accessor as string}
                  className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-zinc-100"
                >
                  {col.footer ? col.footer(sortedData) : null}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export default SortableTable;

type LocalStorageManager<T, R> = {
  read: () => R;
  write: (value: T) => void;
};

function localStorageManager<T>(storageId: string, id: string, defaultValue: T): LocalStorageManager<T, T>;

function localStorageManager<T>(storageId: string, id: string): LocalStorageManager<T, T | undefined>;
function localStorageManager<T>(storageId: string, id: string, defaultValue?: T): LocalStorageManager<T, T | undefined> {
  // Namespaced per table. Legacy unscoped `sortBy` / `sortDir` keys (and the old
  // `<storageId>-<id>` format) are intentionally ignored — sort prefs are low-value.
  const key = `sortable:${storageId}:${id}`;

  return {
    read: () => {
      const value = localStorage.getItem(key);
      try {
        if (value) return JSON.parse(value) as T;
      } catch {
        // Corrupt localStorage entry — fall through to defaultValue.
      }
      return defaultValue as T;
    },
    write: (value: T) => {
      localStorage.setItem(key, JSON.stringify(value));
    },
  };
}
